/**
 * AfeEntrySection — the dedicated **AFE entry** surface Ramon asked for
 * ("sección para AFE entry — Maniobras y Códigos de costo"). It lists the
 * event's existing AFE PLAN lines (via the existing GET `/events/{id}/afe-lines`)
 * and lets an operator add/edit a plan line keyed by **cost code** +
 * **maneuver/phase** with the authorized amount, against the existing
 * POST/PATCH `/events/{event_id}/afe-lines[/{entity_id}]` endpoints.
 *
 * Add + Edit both open a stylized `<Modal>` (the house rule: edits open in a
 * modal, not an inline expanded row). Cost-code / maneuver use the shared
 * `PicklistSelect`. The AFE grid query (`getAfeLines`) is untagged in the shared
 * pApi, so writes here refetch the list explicitly on success rather than
 * relying on tag invalidation. A 403 surfaces "Solo administradores/operadores".
 */
import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { PicklistSelect } from "@components/ui/PicklistSelect";
import { useGetAfeLinesQuery } from "@api/pApi";
import { fmtMoney } from "@lib/format";
import type { AfeLineOut } from "@api/types";
import {
  useCreateAfeLineMutation,
  useUpdateAfeLineMutation,
  type AfeLinePlanCreate,
} from "./costEntryApi";

function isForbidden(err: unknown): boolean {
  return (
    !!err && typeof err === "object" && "status" in err && (err as { status: unknown }).status === 403
  );
}

function emptyDraft(wellUwi: string): AfeLinePlanCreate {
  return {
    well_uwi: wellUwi,
    afe_ref: "",
    cost_code_entry_key: "",
    phase_entry_key: "",
    item_description: "",
    afe_amount: null,
  };
}

function lineToDraft(line: AfeLineOut): AfeLinePlanCreate {
  return {
    well_uwi: line.well_uwi,
    afe_ref: line.afe_ref,
    cost_code_entry_key: line.cost_code_entry_key ?? "",
    phase_entry_key: line.phase_entry_key ?? "",
    item_description: line.item_description ?? "",
    afe_amount: line.afe_amount,
  };
}

function toNum(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function AfeEntrySection({ eventId, wellUwi }: { eventId: string; wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetAfeLinesQuery(eventId, { skip: !eventId });

  const lines = data?.lines ?? [];
  const currency = data && data.currency && data.currency !== "MIXED" ? data.currency : undefined;

  // Modal state: "new" for the add form, an entity_id for an edit, else null.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<AfeLinePlanCreate>(emptyDraft(""));

  const [createLine, createState] = useCreateAfeLineMutation();
  const [updateLine, updateState] = useUpdateAfeLineMutation();
  const busy = createState.isLoading || updateState.isLoading;
  const error = createState.error ?? updateState.error;

  const set = <K extends keyof AfeLinePlanCreate>(k: K, v: AfeLinePlanCreate[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  function openAdd() {
    setDraft(emptyDraft(wellUwi));
    setEditing("new");
  }
  function openEdit(line: AfeLineOut) {
    setDraft(lineToDraft(line));
    setEditing(line.entity_id);
  }

  // AFE lines are keyed under an afe_ref; require it (+ a well) to save.
  const canSave = draft.well_uwi.trim() !== "" && (draft.afe_ref ?? "").trim() !== "" && !busy;

  async function submit() {
    try {
      if (editing === "new") {
        await createLine({ eventId, body: draft }).unwrap();
      } else if (editing) {
        await updateLine({ eventId, entityId: editing, body: draft }).unwrap();
      }
      setEditing(null);
      void refetch();
    } catch {
      // Error shown in the modal footer; keep it open so the entry isn't lost.
    }
  }

  return (
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="card">
        <div className="panel-head-row">
          <h3>{t("afeEntry.title")}</h3>
          <button type="button" className="btn primary" onClick={openAdd}>
            {t("afeEntry.add")}
          </button>
        </div>
        <p className="subtitle" style={{ marginTop: 0 }}>
          {t("afeEntry.subtitle")}
        </p>

        {lines.length === 0 ? (
          <div className="empty-state">{t("afeEntry.empty")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("afeEntry.col.afeRef")}</th>
                  <th>{t("afeEntry.col.costCode")}</th>
                  <th>{t("afeEntry.col.maneuver")}</th>
                  <th>{t("afeEntry.col.description")}</th>
                  <th className="num">{t("afeEntry.col.amount")}</th>
                  <th>{t("afeEntry.col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.entity_id} data-owner="p">
                    <td>{line.afe_ref}</td>
                    <td>{line.cost_code_entry_key ?? "—"}</td>
                    <td>{line.phase_entry_key ?? "—"}</td>
                    <td style={{ color: "var(--dim)", maxWidth: 260 }}>
                      {line.item_description ?? "—"}
                    </td>
                    <td className="num tab-nums">{fmtMoney(line.afe_amount, locale, { currency })}</td>
                    <td>
                      <button type="button" className="btn" onClick={() => openEdit(line)}>
                        {t("costEntry.edit")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={editing !== null}
        title={editing === "new" ? t("afeEntry.add") : t("costEntry.edit")}
        onClose={() => setEditing(null)}
        onConfirm={submit}
        busy={busy}
        canConfirm={canSave}
        confirmLabel={busy ? t("costEntry.saving") : t("costEntry.save")}
        cancelLabel={t("costEntry.cancel")}
        footerNote={
          error ? (isForbidden(error) ? t("costEntry.forbidden") : t("costEntry.error")) : null
        }
      >
        <div className="modal-grid">
          <div className="field">
            <label htmlFor="afe-uwi">{t("afeEntry.field.wellUwi")}</label>
            <input
              id="afe-uwi"
              type="text"
              value={draft.well_uwi}
              onChange={(e) => set("well_uwi", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="afe-ref">{t("afeEntry.col.afeRef")}</label>
            <input
              id="afe-ref"
              type="text"
              value={draft.afe_ref ?? ""}
              onChange={(e) => set("afe_ref", e.target.value)}
            />
          </div>
          <PicklistSelect
            id="afe-code"
            categoryKey="cost_codes"
            label={t("afeEntry.col.costCode")}
            value={draft.cost_code_entry_key ?? ""}
            onChange={(v) => set("cost_code_entry_key", v)}
          />
          <PicklistSelect
            id="afe-maneuver"
            categoryKey="activity_phases"
            label={t("afeEntry.col.maneuver")}
            value={draft.phase_entry_key ?? ""}
            onChange={(v) => set("phase_entry_key", v)}
          />
          <div className="field modal-span-all">
            <label htmlFor="afe-desc">{t("afeEntry.col.description")}</label>
            <input
              id="afe-desc"
              type="text"
              value={draft.item_description ?? ""}
              onChange={(e) => set("item_description", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="afe-amount">{t("afeEntry.col.amount")}</label>
            <input
              id="afe-amount"
              type="number"
              value={draft.afe_amount ?? ""}
              onChange={(e) => set("afe_amount", toNum(e.target.value))}
            />
          </div>
        </div>
      </Modal>
    </QueryState>
  );
}
